import { useCallback } from 'react';
import { fetchQuery, graphql } from 'react-relay';
import { fireErrorAnalytics } from '@jira/platform__error-reporting/src/fireErrorAnalytics.tsx';
import getRelayEnvironment from '@atlassian/jira-relay-environment/src/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { PACKAGE_NAME, TEAM_NAME } from '../../common/constants.tsx';
import type { GroupDetails } from '../../controllers/issue-realtime-updater/index.tsx';
import type { useGetGroupsForIssuesQuery } from './__generated__/useGetGroupsForIssuesQuery.graphql';

type IssueSearchInputType = useGetGroupsForIssuesQuery['variables']['issueSearchInput'];
type StaticViewInputType = useGetGroupsForIssuesQuery['variables']['staticViewInput'];

export const useGetGroupsForIssues = () => {
	const cloudId = useCloudId();

	return useCallback(
		async ({
			issueChanges,
			staticViewInput,
			fieldId,
			issueSearchInput,
			numGroupsLoaded,
		}: {
			issueChanges: {
				issueId: string;
			}[];
			fieldId: string;
			numGroupsLoaded: number;
			issueSearchInput: IssueSearchInputType;
			staticViewInput: StaticViewInputType;
		}): Promise<GroupDetails[]> => {
			const environment = getRelayEnvironment();

			try {
				const data = await fetchQuery<useGetGroupsForIssuesQuery>(
					environment,
					graphql`
						query useGetGroupsForIssuesQuery(
							$cloudId: ID!
							$issueChanges: [JiraIssueChangeInput!]!
							$issueSearchInput: JiraIssueSearchInput!
							$fieldId: String!
							$firstNGroupsToSearch: Int!
							$staticViewInput: JiraIssueSearchStaticViewInput!
						) {
							jira {
								groupsForIssues(
									cloudId: $cloudId
									issueChanges: $issueChanges
									fieldId: $fieldId
									issueSearchInput: $issueSearchInput
									staticViewInput: $staticViewInput
									firstNGroupsToSearch: $firstNGroupsToSearch
									first: 50
								) @optIn(to: "JiraListComponent-M1.2") {
									edges {
										node {
											__id
											id
											afterGroupId
											# eslint-disable-next-line @atlassian/relay/must-colocate-fragment-spreads
											...FirstGroupRow_GroupRow
										}
									}
								}
							}
						}
					`,
					{
						cloudId,
						issueChanges,
						issueSearchInput,
						fieldId,
						// Needs to be connection length + 1 to ensure a potentially new group at the end of the
						// currently loaded connection is included
						firstNGroupsToSearch: numGroupsLoaded + 1,
						staticViewInput,
					},
					{ fetchPolicy: 'network-only' },
				).toPromise();

				return (
					data?.jira?.groupsForIssues?.edges
						?.map((edge) =>
							edge?.node
								? {
										__id: edge.node.__id,
										id: edge.node.id,
										afterGroupId: edge.node.afterGroupId,
									}
								: undefined,
						)
						?.filter(Boolean) ?? []
				);
			} catch (error) {
				fireErrorAnalytics({
					meta: {
						id: 'realTimeFetchGroupsForIssue',
						packageName: PACKAGE_NAME,
						teamName: TEAM_NAME,
					},
					error: error instanceof Error ? error : undefined,
					sendToPrivacyUnsafeSplunk: true,
				});

				return [];
			}
		},
		[cloudId],
	);
};
